import { broadcastLogout } from './crossTabLogout';

// Non-sensitive user-info cache (localStorage) shared by both auth providers
// (legacy UAA and Cognito). Populated from token claims at login/refresh and
// read synchronously across the app (dashboard, websocket, permissions).

// ============ STORAGE KEYS ============
const USER_ID_KEY = 'user_id';
const USER_ORG_ID_KEY = 'user_org_id';
const USER_ROLE_KEY = 'role';
const USER_NAME_KEY = 'user_name';
const USER_FAV_LANGUAGE_KEY = 'fav_lang';
const USER_FAV_LANGUAGE_IS_SET_KEY = 'fav_lang_is_set';
// Whether the user belongs to the tenant's root organization (behind
// isROAdmin). Resolved once from the server after login, cleared at logout.
const USER_ROOT_ORG_MEMBER_KEY = 'root_org_member';

// The role from the token's `authorities` claim (emitted by the
// Pre-Token-Generation Lambda from the user's groups). Tolerates an array, a
// plain string, or a JSON-encoded array — a string claim must never yield a
// first-character "role".
export const roleFromClaims = (claims) => {
    let authorities = claims?.authorities;
    if (typeof authorities === 'string') {
        try { authorities = JSON.parse(authorities); } catch { /* plain role name */ }
    }
    const first = Array.isArray(authorities) ? authorities[0] : authorities;
    return typeof first === 'string' && first ? first.toLowerCase() : undefined;
};

// ============ GETTERS ============
export const getUserId = () => localStorage.getItem(USER_ID_KEY);
export const getUserOrgId = () => localStorage.getItem(USER_ORG_ID_KEY);
export const getUserRole = () => localStorage.getItem(USER_ROLE_KEY);
export const getUserName = () => localStorage.getItem(USER_NAME_KEY);
export const getUserFavLanguage = () => localStorage.getItem(USER_FAV_LANGUAGE_KEY);
export const getUserFavLanguageIsSet = () => localStorage.getItem(USER_FAV_LANGUAGE_IS_SET_KEY);
// null = not resolved yet; otherwise true/false
export const getUserRootOrgMember = () => {
    const value = localStorage.getItem(USER_ROOT_ORG_MEMBER_KEY);
    return value === null ? null : value === 'true';
};

// Language for Cognito managed-login pages (lang query parameter): the user's
// favorite language when known, otherwise the browser language. Restricted to
// the languages the app itself supports.
export const getCognitoLoginLang = () => {
    const raw = getUserFavLanguage() || (typeof navigator !== 'undefined' ? navigator.language : '') || '';
    return raw.toLowerCase().substring(0, 2) === 'fr' ? 'fr' : 'en';
};

// ============ CROSS-TAB: SESSION ESTABLISHED ============
// Fires in the *other* tabs when a login completes somewhere (the user id is
// written after a sign-out cleared it). Lets a tab on /signed-out enter the
// app without a login flow of its own (Amplify's tokens are shared).
export const onSessionEstablished = (callback) => {
    if (typeof window === 'undefined') return () => { };
    const handler = (event) => {
        if (event.key === USER_ID_KEY && event.newValue !== null) callback(event);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
};

// ============ SETTERS ============
export const setUserFavLanguage = (lang) => localStorage.setItem(USER_FAV_LANGUAGE_KEY, lang);
export const setUserFavLanguageIsSet = (value) => localStorage.setItem(USER_FAV_LANGUAGE_IS_SET_KEY, value);
export const setUserRootOrgMember = (isMember) => localStorage.setItem(USER_ROOT_ORG_MEMBER_KEY, isMember ? 'true' : 'false');

export const storeUserInfo = ({ userId, organizationId, role, userName, language }) => {
    if (userId != null) localStorage.setItem(USER_ID_KEY, userId);
    if (organizationId != null) localStorage.setItem(USER_ORG_ID_KEY, organizationId);
    if (role != null) localStorage.setItem(USER_ROLE_KEY, role);
    if (userName != null) localStorage.setItem(USER_NAME_KEY, userName);
    if (language != null) {
        localStorage.setItem(USER_FAV_LANGUAGE_KEY, language);
        localStorage.setItem(USER_FAV_LANGUAGE_IS_SET_KEY, 'false');
    }
};

// ============ CLEAR ============
// Session data only. The favourite language is a device preference: it is
// kept across sign-outs so the login page and the next session keep the
// language the user chose on this browser.
export const clearUserInfo = () => {
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(USER_ORG_ID_KEY);
    localStorage.removeItem(USER_ROLE_KEY);
    localStorage.removeItem(USER_NAME_KEY);
    localStorage.removeItem(USER_ROOT_ORG_MEMBER_KEY);
};

/**
 * Clear everything (user info, session/cache storage) and broadcast the
 * logout to other tabs. Token invalidation itself is provider-specific and
 * handled by the active token store, as is the reaction of the other tabs
 * (see installCrossTabLogoutListener in each store).
 */
export const clearAllAuthData = () => {
    clearUserInfo();

    broadcastLogout();

    sessionStorage.clear();

    if ('caches' in window) {
        caches.keys().then(keys => {
            keys.forEach(key => caches.delete(key));
        });
    }
};
